import { engineLabels } from 'Utils';
import {
  FILE_SOURCE_TYPE,
  isFileDatasourceType,
  isSaasSource,
  isPlatformManaged,
  needsCredentials,
  platformOwnerKind,
  isEligibleMaskTarget,
} from 'DataSources/datasourcesApi';

/**
 * Pure helpers behind the shared SourcePicker: how a row is labelled, located, searched and
 * classified, what its connection status is, and the restriction rules screens pass in.
 * Prototype: docs/prototypes/data-source-picker.html.
 */

/** A successful test older than this reads as stale: still selectable, flagged for a retest. */
export const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export const KINDS = [
  ['database', 'Databases'],
  ['file', 'Files'],
  ['saas', 'SaaS'],
];

export const kindOf = (c) => {
  if (!c) return 'database';
  if (c.type === FILE_SOURCE_TYPE || isFileDatasourceType(c.datasourceType)) return 'file';
  if (isSaasSource(c)) return 'saas';
  return 'database';
};

const FILE_LABEL = {
  FILE_DELIMITED_FOLDER: 'Delimited',
  FILE_PARQUET_FOLDER: 'Parquet',
  FILE_GENERIC_FOLDER: 'Folder Search',
  FILE_DOCUMENT_FOLDER: 'Documents',
  FILE_JSON_FOLDER: 'JSON',
  FILE_XML_FOLDER: 'XML',
  FILE_EXCEL_FOLDER: 'Excel',
  FILE_FIXED_WIDTH_FOLDER: 'Fixed width',
};

export const engineText = (c) => {
  if (!c) return '';
  if (kindOf(c) === 'file') {
    const scheme = String(c.vfsScheme || '').toLowerCase();
    return `${FILE_LABEL[c.datasourceType] || 'Files'}${scheme ? ` · ${scheme}` : ''}`;
  }
  const type = c.type || c.engine || '';
  return engineLabels[type] || type;
};

/** Where the row points: host / database, a folder path, or a SaaS login URL / user. */
export const locationOf = (c) => {
  if (!c) return { main: '', sub: '' };
  const kind = kindOf(c);
  if (kind === 'file') return { main: c.rootPath || '', sub: '' };
  if (kind === 'saas') return { main: String(c.loginUrl || '').replace(/^https?:\/\//, ''), sub: c.username || '' };
  return { main: c.host || '', sub: c.database || '' };
};

const haystack = (c) =>
  [c.name, c.type, engineText(c), c.host, c.database, c.schemaName, c.rootPath, c.loginUrl, c.username]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

/** Every whitespace-separated term must appear somewhere in the row. */
export const matchesQuery = (c, query) => {
  const terms = String(query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const h = haystack(c);
  return terms.every((t) => h.includes(t));
};

const toMs = (v) => {
  if (v == null || v === '') return null;
  const ms = typeof v === 'number' ? v : Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * The connection status the picker shows, from what the platform already stored — never from a
 * fresh test. `override` is the picker's own result for this row (a retest done while it is open).
 *   managed  platform-owned row: the API refuses a test, TDM owns its connection
 *   credentials  no credentials on file (imported row): a test can only fail
 *   testing  a retest is in flight
 *   online   last test succeeded (`stale` when older than staleAfterMs)
 *   offline  last test failed
 *   untested never tested
 */
export const statusOf = (c, override, now = Date.now(), staleAfterMs = STALE_AFTER_MS) => {
  if (isPlatformManaged(c)) return { state: 'managed', owner: platformOwnerKind(c) };
  if (override) return override;
  if (needsCredentials(c)) return { state: 'credentials' };
  const testedAt = toMs(c && c.lastTestAt);
  if (c && c.lastTestSuccess === true) {
    return { state: 'online', testedAt, stale: testedAt == null || now - testedAt > staleAfterMs };
  }
  if (c && c.lastTestSuccess === false) {
    // AppContext rows carry the stored verdict's message (mergeLastTestIntoUiRow); others fetch it.
    return { state: 'offline', testedAt, error: (c.status === 'FAILED' && (c.statusMessage || c.message)) || null };
  }
  return { state: 'untested' };
};

/** A row the user may pick: anything but a known failure or a test still running. */
export const isPickable = (status) => !['offline', 'testing', 'credentials'].includes(status.state);

/** A row "Retest stale" would test: tested more than staleAfterMs ago, or never tested. */
export const needsRecheck = (status, now = Date.now(), staleAfterMs = STALE_AFTER_MS) => {
  if (status.state === 'untested') return true;
  if (status.state !== 'online' && status.state !== 'offline') return false;
  return status.testedAt == null || now - status.testedAt > staleAfterMs;
};

const STATUS_RANK = { online: 0, managed: 1, untested: 2, testing: 2, offline: 3, credentials: 4 };

export const sortRows = (rows, key, dir, statusById) => {
  const value = (c) => {
    if (key === 'status') return STATUS_RANK[(statusById[c.id] || {}).state] ?? 9;
    if (key === 'engine') return engineText(c).toLowerCase();
    if (key === 'location') return locationOf(c).main.toLowerCase();
    return String(c.name || '').toLowerCase();
  };
  return rows.slice().sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va < vb) return -dir;
    if (va > vb) return dir;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
};

/**
 * Split `sources` into the rows the picker offers and the ones it leaves out, with the reason
 * for each. A restriction is `{ label, test(row), why(row) }`; the first one a row fails names
 * it. `exclude` ids (already chosen elsewhere) are left out as "Already added".
 */
export const applyRestrictions = (sources, restrict = [], exclude = []) => {
  const excluded = new Set((exclude || []).map(String));
  const pool = [];
  const hidden = [];
  (sources || []).forEach((s) => {
    if (!s || s.id == null) return;
    if (excluded.has(String(s.id))) {
      hidden.push({ source: s, why: 'Already added' });
      return;
    }
    const failed = (restrict || []).find((r) => !r.test(s));
    if (failed) {
      hidden.push({ source: s, why: failed.why ? failed.why(s) : failed.label });
      return;
    }
    pool.push(s);
  });
  return { pool, hidden };
};

const ownerWhy = (c) => (platformOwnerKind(c) === 'raw-view' ? 'Managed by TDM (subset raw view)' : 'Managed by TDM (staging)');

/**
 * Restrictions screens compose. Each wraps the existing predicate (datasourcesApi / tdmUtils)
 * rather than restating it, so the picker cannot drift from the rule the server enforces.
 */
export const RULES = {
  /** filterJobStartable, per row: credentials on file and not platform-managed. */
  jobStartable: {
    label: 'Ready to run (credentials on file)',
    test: (c) => !needsCredentials(c) && !isPlatformManaged(c),
    why: (c) => (isPlatformManaged(c) ? ownerWhy(c) : 'Needs credentials: open the source and save them'),
  },
  /** filterActionableConnections, per row: credentials on file (platform-managed rows stay). */
  actionable: {
    label: 'Credentials on file',
    test: (c) => !needsCredentials(c),
    why: () => 'Needs credentials: open the source and save them',
  },
  writable: {
    label: 'Writable only',
    test: (c) => !c.readOnly,
    why: () => 'Read-only (source of record)',
  },
  notSaas: {
    label: 'No SaaS',
    test: (c) => !isSaasSource(c),
    why: (c) => `${engineText(c)} is fingerprint and discover only`,
  },
  notEngine: (engine, reason) => ({
    label: `No ${engineLabels[engine] || engine}`,
    test: (c) => String(c.type || '').toLowerCase() !== engine,
    why: () => reason || `${engineLabels[engine] || engine} is not supported here`,
  }),
  /** Only ids in `ids` (a Set of numeric ids); `why` explains the rest. */
  inSet: (ids, label, why) => ({
    label,
    test: (c) => ids.has(Number(c.id)),
    why: () => why,
  }),
  /** isEligibleMaskTarget(source, row). */
  maskTargetFor: (source) => ({
    label: `Can receive masked ${engineText(source)} data`,
    test: (c) => isEligibleMaskTarget(source, c),
    why: (c) => {
      if (c.id === source.id) return 'The source itself';
      if (c.readOnly) return 'Read-only (source of record)';
      if (isPlatformManaged(c)) return ownerWhy(c);
      if (kindOf(c) !== 'database') return `${engineText(c)} cannot receive masked rows`;
      if (String(c.type || '').toLowerCase() !== String(source.type || '').toLowerCase()) {
        return `${engineText(c)}, not ${engineText(source)}`;
      }
      return 'Same database and schema as the source';
    },
  }),
  /** Any predicate with its own wording. */
  custom: (label, test, why) => ({ label, test, why: why || (() => label) }),
};
